import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import MFAVerification from '../components/MFAVerification';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
    organization_name: '',
    invite_code: ''
  });
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaTempToken, setMfaTempToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      // Attach any saved "remember this device" MFA trust token so a
      // recently-verified user is skipped past the 6-digit code entry step.
      const savedTrustToken = isLogin
        ? localStorage.getItem(`mfa_trust_token_${formData.email.toLowerCase()}`)
        : null;
      const payload = isLogin
        ? {
            email: formData.email,
            password: formData.password,
            ...(savedTrustToken ? { mfa_trust_token: savedTrustToken } : {}),
          }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);

      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setMfaUserId(response.data.user_id);
        setMfaTempToken(response.data.temp_token);
        toast.info('Two-factor authentication required');
        return;
      }

      // Check if MFA setup is required (user hasn't enrolled yet)
      if (response.data.mfa_setup_required) {
        login(response.data.token, response.data.user);
        toast.warning('MFA is required. Please set it up to continue.', { duration: 6000 });
        navigate('/mfa-setup');
        return;
      }

      // A successful MFA-backed login rotates the trust token — persist it
      // so the browser stays remembered for the next 7 days.
      if (response.data.mfa_trust_token) {
        localStorage.setItem(
          `mfa_trust_token_${response.data.user.email.toLowerCase()}`,
          response.data.mfa_trust_token,
        );
      }

      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Login successful' : 'Registration successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (data) => {
    if (data.mfa_trust_token && data.user?.email) {
      localStorage.setItem(
        `mfa_trust_token_${data.user.email.toLowerCase()}`,
        data.mfa_trust_token,
      );
    }
    login(data.token, data.user);
    toast.success('Login successful');
    navigate('/');
  };

  const handleMFABack = () => {
    setMfaRequired(false);
    setMfaUserId(null);
    setMfaTempToken(null);
    setFormData({ ...formData, password: '' });
  };

  // Show MFA verification screen if required
  if (mfaRequired && mfaUserId && mfaTempToken) {
    return (
      <MFAVerification
        userId={mfaUserId}
        tempToken={mfaTempToken}
        onSuccess={handleMFASuccess}
        onBack={handleMFABack}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1574848296471-28f79a036f79?w=1200)',
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-8">
            <ShieldCheck className="w-12 h-12 text-white" />
            <div>
              <h1 className="text-3xl font-bold text-white font-heading tracking-tight leading-tight">
                Cyber Forte
              </h1>
              <p className="text-lg text-white/80 font-medium">Compliance Manager</p>
            </div>
          </div>
          <p className="text-xl text-white/90 max-w-md">
            Automated Compliance Management
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <ShieldCheck className="w-10 h-10 text-slate-900" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-heading">Cyber Forte</h1>
              <p className="text-sm text-slate-600">Compliance Manager</p>
            </div>
          </div>
          
          <div className="bg-white border border-border rounded-sm p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-semibold mb-2" data-testid="auth-title">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h2>
              <p className="text-sm text-slate-600">
                {isLogin
                  ? 'Enter your credentials to access your account'
                  : 'Fill in the details to create a new account'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1.5 rounded-sm"
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1.5 rounded-sm"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1.5 rounded-sm"
                  required
                />
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="invite_code" className="text-sm font-medium">
                    Invite Code
                  </Label>
                  <Input
                    id="invite_code"
                    type="text"
                    placeholder="Enter 8-character invite code"
                    value={formData.invite_code}
                    onChange={(e) => setFormData({ ...formData, invite_code: e.target.value })}
                    className="mt-1.5 rounded-sm"
                    required
                    maxLength={8}
                    data-testid="signup-invite-code-input"
                  />
                  <p className="text-xs text-slate-600 mt-1">
                    Join your organization using the invite code from your admin.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                data-testid="submit-button"
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                disabled={loading}
              >
                {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-slate-600 hover:underline"
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}